import { useState, useEffect } from 'react';
import { AbstractForm, AbstractFormState, IStateForm } from '../../../common/AbstractForm';
import FormModel from '../../../../utils/FormModel';
import AWSAccountConnectivityConfiguration from '../../../../models/entities/AWSAccountConnectivityConfiguration';
import AWSAccountSubnet from '../../../../models/entities/AWSAccountSubnet';
import { isIpAddressInputValid } from '../../../common/form-controls/IpAddressValidator';

export interface IConnectivityConfigurationStateForm extends IStateForm {
	region: string;
	priority: number;
	cidr: string;
	tags: { [key: string]: string };
	size: string;
	route53ResolverRuleAssociations: string;
	enableS3GatewayEndpoint: boolean;
	editingConfiguration?: AWSAccountConnectivityConfiguration;
	subnets: AWSAccountSubnet[];
}

export interface RulesFormProps {
	editingConfiguration?: AWSAccountConnectivityConfiguration;
	isReadOnly?: boolean;
}

export interface AbstractConnectivityConfigurationFormState<T> extends AbstractFormState<T> {}

export const useAbstractConnectivityConfigurationForm = (editingConfiguration) => {
	const [state, setState] = useState<AbstractConnectivityConfigurationFormState<IConnectivityConfigurationStateForm>>(
		{
			stateForm:
				editingConfiguration != null
					? { ...editingConfiguration }
					: {
							region: [],
							priority: 1,
							cidr: '',
							size: '',
							tags: null,
							route53ResolverRuleAssociations: null,
							enableS3GatewayEndpoint: null,
							subnets: [],
						},
		}
	);

	const abstractFormInstance = new (class extends AbstractForm<
		RulesFormProps,
		AbstractConnectivityConfigurationFormState<IConnectivityConfigurationStateForm>
	> {
		constructor() {
			super({});
			this.state = state;
		}

		componentDidUpdate(prevProps) {
			if (this.props.editingConfiguration !== prevProps.editingConfiguration) {
				if (this.props.editingConfiguration != null) this.setState({ stateForm: {...this.getRuleCopy(), size: this.state.stateForm.size} });
				else this.setState({ stateForm: this.getDefaultForm() });
			}
		}

		getRuleCopy() {
			return { ...this.props.editingConfiguration };
		}

		getDefaultForm() {
			return {
				region: '',
				priority: 1,
				cidr: '',
				size: '',
				tags: null,
				route53ResolverRuleAssociations: null,
				enableS3GatewayEndpoint: null,
				subnets: [],
			};
		}
	})();

	const [formModel] = useState<FormModel>(
		(abstractFormInstance.formModel = new FormModel({
			region: {
				getValue: () => state.stateForm.region,
				validation: {
					required: () => true,
				},
			},
			priority: {
				getValue: () => state.stateForm.priority,
				validation: {
					//TODO: validate priority order
					required: () => true,
				},
			},
			size: {
				getValue: () => state.stateForm.size,
			},
			enableS3GatewayEndpoint: {
				getValue: () => state.stateForm.enableS3GatewayEndpoint,
				validation: {
					required: () => true,
				},
			},
			cidr: {
				getValue: () => state.stateForm.cidr,
				validation: {
					required: () => true,
					rules: [
						{
							assert: () => isIpAddressInputValid(state.stateForm.cidr, false, true),
							message: 'Not a valid IP address.',
						},
					],
				},
			},
			tags: {
				getValue: () => state.stateForm.tags,
				validation: {
					required: () => true,
					rules: [
						{
							assert: () =>
								Object.keys(state.stateForm.tags || {}).length > 0 &&
								Object.keys(state.stateForm.tags).some(
									(key, value) => key !== '' || state.stateForm.tags[key] !== ''
								),
							message: 'At least one tag is required.',
						},
					],
				},
			},
			route53ResolverRuleAssociations: {
				getValue: () => state.stateForm.route53ResolverRuleAssociations,
			},
			subnets: {
				getValue: () => state.stateForm.subnets,
			},
		}))
	);

	const handleStateFormChange = (field: any, value: any) => {
		abstractFormInstance.setState = (newState) => setState((prevState) => ({ ...prevState, ...newState }));
		abstractFormInstance.handleStateFormChange(field, value);
	};

	const getStateFormHandlers = () => {
		return {
			region: {
				value: state.stateForm.region,
				validation: formModel.fields.region.validation,
				onChange: (e) => {
					handleStateFormChange('region', e.target.value);
				},
			},
			priority: {
				value: state.stateForm.priority.toString(),
				validation: formModel.fields.priority.validation,
				onChange: (e) => {
					handleStateFormChange('priority', parseInt(e.target.value));
				},
			},
			size: {
				value: state.stateForm.size,
				onChange: (e) => {
					handleStateFormChange('size', e);
				},
			},
			enableS3GatewayEndpoint: {
				value: state.stateForm.enableS3GatewayEndpoint,
				validation: formModel.fields.enableS3GatewayEndpoint.validation,
				onChange: (e) => {
					handleStateFormChange('enableS3GatewayEndpoint', e.target.value);
				},
			},
			cidr: {
				value: state.stateForm.cidr,
				validation: formModel.fields.cidr.validation,
				onChange: (e) => {
					handleStateFormChange('cidr', e.target.value);
				},
			},
			tags: {
				value: state.stateForm.tags,
				validation: formModel.fields.tags.validation,
				onChange: (e) => {
					handleStateFormChange('tags', e);
				},
			},
			route53ResolverRuleAssociations: {
				value: state.stateForm.route53ResolverRuleAssociations,
				validation: formModel.fields.route53ResolverRuleAssociations.validation,
				onChange: (e) => {
					handleStateFormChange('route53ResolverRuleAssociations', e.target.value);
				},
			},
			subnets: {
				value: state.stateForm.subnets,
				onChange: (subnets: AWSAccountSubnet[]) => {
					handleStateFormChange('subnets', subnets);
				},
			},
		};
	};

	const getDefaultForm = () => {
		return {
			region: '',
			priority: 1,
			cidr: '',
			size: '',
			tags: null,
			route53ResolverRuleAssociations: '',
			enableS3GatewayEndpoint: false,
			subnets: [],
		};
	};

	const isValid = () => {
		return formModel.isValid();
	};

	return {
		state,
		formModel,
		setState,
		handleStateFormChange,
		getStateFormHandlers,
		isValid,
		getDefaultForm,
	};
};
